import React, { useCallback, useState } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme, Tabs, Tab } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import { DynamicControls } from '../../core/NodeControls';
import { getLayerParameters } from '../../utils/tensorflowParameters';

/**
 * LSTMNode represents a Long Short-Term Memory layer in the neural network
 */
const LSTMNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  // Get the TensorFlow parameters for the LSTM layer
  const lstmParams = getLayerParameters('LSTM');
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Units: {data.units || 32}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          <Chip 
            label={`act: ${data.activation || 'tanh'}`}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
            }}
          />
          
          <Chip 
            label={data.return_sequences ? 'return seq' : 'last state'}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
            }}
          />
        </Box>
        
        {/* Visual representation of LSTM memory cell */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            justifyContent: 'center' 
          }}
        >
          <Box sx={{ 
            width: '100%', 
            height: '2px', 
            backgroundColor: theme.palette.divider,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              left: 0,
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: theme.palette.secondary.main,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              right: 0,
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: theme.palette.secondary.main,
            }
          }} />
        </Box>
      </>
    );
  };
  
  // Define which parameters should appear in the basic tab
  const basicParams = [
    'units',
    'activation', 
    'recurrent_activation', 
    'return_sequences', 
    'return_state',
    'dropout', 
    'recurrent_dropout'
  ];
  
  // Controls for the node configuration popover with tabs for basic and advanced options
  const renderControls = () => {
    return (
      <>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          <Tab label="Basic" />
          <Tab label="Advanced" />
        </Tabs>
        
        {/* Basic parameters tab */}
        {tabValue === 0 && (
          <DynamicControls
            parameters={lstmParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => basicParams.includes(key)}
          />
        )}
        
        {/* Advanced parameters tab */}
        {tabValue === 1 && (
          <DynamicControls
            parameters={lstmParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => !basicParams.includes(key)}
          />
        )}
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<MemoryIcon fontSize="small" />}
      nodeType="recurrent"
      preview={renderPreview()}
      controls={renderControls()}
      onDelete={data.onDelete}
    />
  );
};

export default memo(LSTMNode); 